import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output, ViewChild
} from "@angular/core";
import {Visit, VisitUI} from "../models/visit";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {PatientService} from "../../patient/services/patient.service";
import {ElementSize} from "../../../../dev/components/elad.forms2/interfaces/layout";
import {ComboBoxComponent} from "@progress/kendo-angular-dropdowns";
import {LABEL_SUFFIX, TranslateService} from "../../../../common/translate";
import {SystemConfigurationValues} from "../../../../common/configuration/services/system.configuration.utils";
import {LogCat, LogManager} from "../../../../common/log";

@UntilDestroy()
@Component({
	selector: 'cp-visit-selection',
	templateUrl: './visit.selection.template.html',
	styleUrls: ['./visit.selection.style.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitSelectionComponent implements OnInit, OnDestroy {

	@Input() patientPersonKey: number;
	@Input() currentVisit: Visit;
	@Input() layout;
	@Input() control;

	@Output() visitSelectionChanged: EventEmitter<Visit> = new EventEmitter<Visit>();
	@ViewChild("visitCombobox", {static: false}) visitCombobox: ComboBoxComponent;

	elementSize = ElementSize;
	public patientVisits:  VisitUI[];
	public filteredPatientVisits: VisitUI[];
	public selectedVisit: Visit;

	public pendingSelectedVisit: Visit;
	private hideVisitChangeWarning: boolean = false;

	constructor(private patientService: PatientService,
				private cd: ChangeDetectorRef,
				private translateService: TranslateService,
				private systemConfigurationValues: SystemConfigurationValues) {
	}

	ngOnDestroy(): void {
	}

	ngOnInit(): void {
		this.loadPatientVisits();
		this.hideVisitChangeWarning = this.systemConfigurationValues.getBoolean('HideChangeVisitWarningMessage');
	}

	onVisitChangeOk() {
		this.selectedVisit = this.pendingSelectedVisit;
		this.pendingSelectedVisit = null;
		this.control.setValue(this.selectedVisit.visitNumber, {emitViewToModelChange: false});
		this.visitSelectionChanged.emit(this.selectedVisit);
	}

	onVisitChangeCanceled() {
		this.pendingSelectedVisit = null;
	}

	onVisitSelectionChange(visitToSelect: Visit) {
		if (this.hideVisitChangeWarning) {
			this.selectedVisit = visitToSelect;
			this.control.setValue(this.selectedVisit.visitNumber, {emitViewToModelChange: false});
			this.visitSelectionChanged.emit(this.selectedVisit);
		} else { //Show warning popup
			this.pendingSelectedVisit = visitToSelect;
			//Rollback Kendo's selection to the previously selected visit.
			//We will do the actual value change only after the user confirms the update
			this.visitCombobox.writeValue(this.selectedVisit);
		}
	}

	onVisitFilterChange(visitNumFilter: any) {
		if (!visitNumFilter) {
			this.filteredPatientVisits = this.patientVisits;
		} else {
			this.filteredPatientVisits = this.patientVisits.filter(visit => !visit.isNewVisit && visit.visitNumber.startsWith(visitNumFilter));
		}
	}

	onClick(event) {
		if (!this.visitCombobox.isOpen && event && event.srcElement && event.srcElement.nodeName === 'INPUT') {
			this.visitCombobox.toggle(true);
		}
	}

	private loadPatientVisits() {
		this.patientService.getPatientVisitList(this.patientPersonKey).pipe(untilDestroyed(this)).subscribe(result => {

			if (this.currentVisit) {
				let existingCurrentVisit = result.find(visit => visit.visitNumber === this.currentVisit.visitNumber);

				if (existingCurrentVisit) { //current visit is not a new visit
					this.patientVisits = result.map((visit) => this.initializeDescription(visit));
					this.selectedVisit = existingCurrentVisit;
				} else { //current visit is a new visit so add it to the list
					let newVisitItem = Object.assign(this.currentVisit, {isNewVisit: true});
					this.patientVisits = [newVisitItem, ...result].map((visit) => this.initializeDescription(visit));
					this.selectedVisit = newVisitItem;
				}
			}
			this.filteredPatientVisits = this.patientVisits;

			if (this.selectedVisit) {
				this.control.setValue(this.selectedVisit.visitNumber, {emitViewToModelChange: false});
			}
			LogManager.debug(LogCat.Clinical.Visit.Form, "Got patient visits for patient key:", this.patientPersonKey, this.patientVisits);

			this.cd.markForCheck();
		});
	}

	private initializeDescription(visit: VisitUI): VisitUI {
		visit.visitDescription = visit.visitNumber;
		if (visit.isNewVisit) {
			visit.visitDescription += ` (${this.translateService.instant('NewVisit' + LABEL_SUFFIX)})`;
		}

		return visit;
	}
}
